import { ResponsesCellRenderer } from "./ResponsesCellRenderer";
import { MANUAL_RESPONSE_OPTIONS } from "./types";

import type {
  CarrierRef,
  CoverageRef,
  ResponseCell,
  ResponsesTableResponse,
} from "@platform/api";
import type { ColDef } from "ag-grid-community";
import type { DisplayStatus, ResponsesGridRow } from "./types";

/**
 * Builds the sparse cell lookup used during row construction.
 *
 * The outer key is `carrierId`; the inner key is the column's `planGroupId`.
 */
const buildCellLookup = (
  cells: ResponseCell[],
): Map<string, Map<string, ResponseCell>> =>
  cells.reduce((acc, cell) => {
    if (!acc.has(cell.carrierId)) {
      acc.set(cell.carrierId, new Map());
    }
    acc.get(cell.carrierId)?.set(cell.planGroupId, cell);
    return acc;
  }, new Map<string, Map<string, ResponseCell>>());

/**
 * Maps the sparse API cell data (ProposalStatus + CoverageStatus) into the
 * human-readable display label shown in the grid.
 *
 * Rules (in evaluation order):
 * - No cell present → `"Pending Proposal"` (API sparse default; matches the
 *   `MANUAL_RESPONSE_OPTIONS` dropdown value an analyst would pick)
 * - `proposalStatus === 'rejected'` → `"DTQ"`
 * - `proposalStatus === 'withdrawn'` → `"Withdrawn"`
 * - `proposalStatus === 'pending'` → `"Pending Proposal"`
 * - `proposalStatus === 'received'` + `coverageStatus === 'declinedToQuote'` → `"DTQ"`
 * - `proposalStatus === 'received'` + `coverageStatus === 'quoted'` + incumbent → `"Incumbent"`
 * - `proposalStatus === 'received'` + `coverageStatus === 'quoted'` → `"Proposal Received"`
 * - Fallback → `"Pending"`
 */
export const resolveDisplayStatus = (
  cell: ResponseCell | undefined,
  isIncumbent: boolean,
): DisplayStatus => {
  if (!cell) return "Pending Proposal";

  // Analyst-assigned override takes precedence over computed status.
  if (cell.manualStatus) return cell.manualStatus;

  const { proposalStatus, coverageStatus } = cell;

  if (proposalStatus === "rejected") return "DTQ";
  if (proposalStatus === "withdrawn") return "Withdrawn";
  if (proposalStatus === "pending") return "Pending Proposal";

  if (proposalStatus === "received") {
    if (coverageStatus === "declinedToQuote") return "DTQ";
    if (coverageStatus === "quoted") {
      return isIncumbent ? "Incumbent" : "Proposal Received";
    }
  }

  return "Pending";
};

const sortbySequence = <T extends { sequence: number }>(a: T, b: T): number =>
  a.sequence - b.sequence;

/**
 * Converts a `ResponsesTableResponse` into AG Grid row data.
 *
 * Carriers are sorted by `sequence` ascending. Each coverage becomes a
 * dynamic property on the row keyed by the coverage's `planGroupId`.
 * Missing cells default to `"Pending Proposal"` per API contract.
 */
export const buildResponsesRowData = (
  response: ResponsesTableResponse,
): ResponsesGridRow[] => {
  const { carriers, coverages, cells } = response;
  const lookup = buildCellLookup(cells);

  const sortedCarriers = [...carriers].sort(sortbySequence);

  return sortedCarriers.map((carrier: CarrierRef): ResponsesGridRow => {
    const carrierCells = lookup.get(carrier.id);

    const row: ResponsesGridRow = {
      carrierId: carrier.id,
      carrierName: carrier.name,
      isIncumbent: carrier.isIncumbent,
    };

    coverages.forEach((coverage: CoverageRef) => {
      const cell = carrierCells?.get(coverage.planGroupId);
      row[coverage.planGroupId] = resolveDisplayStatus(
        cell,
        carrier.isIncumbent ?? false,
      );
    });

    return row;
  });
};

/**
 * Builds AG Grid column definitions from the ordered coverage list.
 *
 * First column: carrier name, pinned left. Row order is fixed to the
 * carrier sequence applied from the Quotes tab (CD-1806) — no row drag
 * handle here, since a local reorder on this page was never propagated
 * to the other SBS pages and silently overrode future Quotes-tab reorders.
 * Remaining columns: one per coverage, sorted by `sequence` ascending.
 */
export const buildResponsesColDefs = (
  coverages: CoverageRef[],
): ColDef<ResponsesGridRow>[] => {
  const sortedCoverages = [...coverages].sort(sortbySequence);

  const carrierCol: ColDef<ResponsesGridRow> = {
    field: "carrierName",
    headerName: "Carrier",
    pinned: "left",
    lockPinned: true,
    lockVisible: true,
    filter: "agSetColumnFilter",
    minWidth: 180,
    flex: 0,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    suppressHeaderFilterButton: true,
    cellStyle: {
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
  };

  const coverageCols: ColDef<ResponsesGridRow>[] = sortedCoverages.map(
    (coverage: CoverageRef) => ({
      field: coverage.planGroupId,
      headerName: coverage.label,
      minWidth: 140,
      flex: 1,
      sortable: false,
      editable: true,
      singleClickEdit: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: MANUAL_RESPONSE_OPTIONS,
        allowTyping: true,
        filterList: true,
        searchType: "matchAny",
      },
      cellRenderer: ResponsesCellRenderer,
    }),
  );

  return [carrierCol, ...coverageCols];
};
